import { jingleByIdQuery, jingleCreate, jingleUpdate } from '@/entities/tracks'
import { type UploadFileWithId } from '@/features/upload-files'
import type {
  PutApiTracksIdMutationRequest,
  TrackModel,
  UpdateTrackModel,
} from '@/shared/api'
import { ROUTES } from '@/shared/config'
import { createModalModel } from '@/shared/lib/model-factories'
import { navigateToFx } from '@/shared/lib/providers'
import { createEvent, createStore, sample } from 'effector'
import { createGate } from 'effector-react'
import { or } from 'patronum'

export type JingleFormModel = UpdateTrackModel & {
  files?: UploadFileWithId[]
}

type JingleFormGateProps = {
  isModal: boolean
  onSubmitted?: (jinlgeId: string) => void
  resetForm?: () => void
}

export const JingleFormGate = createGate<JingleFormGateProps>()

// Form data

const $jingleFormJingleData = createStore<TrackModel | null>(null)
export const jingleFormJingleDataChanged = createEvent<TrackModel | null>()
$jingleFormJingleData.on(
  jingleFormJingleDataChanged,
  (_, albumCollection) => albumCollection,
)

// Modal

export const {
  modalOpened: jingleFormModalOpened,
  modalClosed: jingleFormModalClosed,
  $isModalOpened: $isJingleFormModalOpened,
} = createModalModel()

const jingleFormModalOpenedForEdit = createEvent<{ id: string }>()
sample({
  clock: jingleFormModalOpened,
  filter: (params): params is { id: string } => Boolean(params),
  target: jingleFormModalOpenedForEdit,
})

sample({
  clock: jingleFormModalOpenedForEdit,
  target: jingleByIdQuery.start,
})

sample({
  source: JingleFormGate.state,
  clock: jingleFormModalClosed,
  fn: (state) => {
    state.resetForm?.()
  },
})

sample({
  source: jingleByIdQuery.$data,
  clock: jingleFormModalClosed,
  target: jingleByIdQuery.reset,
})

// Sumbit

export const jingleFormSubmitted = createEvent<UpdateTrackModel>()
export const $jingleFormSubmitting = or(
  jingleCreate.$pending,
  jingleUpdate.$pending,
)

// Create Jingle

sample({
  source: jingleByIdQuery.$status,
  clock: jingleFormSubmitted,
  filter: (jingleByIdQueryStatus) => jingleByIdQueryStatus === 'initial',
  fn: (
    _,
    formData,
  ): {
    data: PutApiTracksIdMutationRequest
  } => {
    return {
      data: formData,
    }
  },
  target: jingleCreate.start,
})

// Update Jingle

sample({
  source: jingleByIdQuery.$data,
  clock: jingleFormSubmitted,
  filter: (jingle: TrackModel | null): jingle is TrackModel => {
    return jingle !== null && typeof jingle.id === 'string'
  },
  fn: (jingle, formData) => {
    return { id: jingle.id, data: formData }
  },
  target: jingleUpdate.start,
})

// OnSubmitted

sample({
  source: JingleFormGate.state,
  clock: [jingleCreate.finished.success, jingleUpdate.finished.success],
  fn: (gateState, { result }) => {
    if (result?.id && gateState.onSubmitted) {
      gateState.onSubmitted(result.id)
    }
  },
})

sample({
  source: JingleFormGate.state,
  clock: [jingleCreate.finished.success, jingleUpdate.finished.success],
  filter: (state: JingleFormGateProps) => state.isModal,
  target: jingleFormModalClosed,
})

const AudioRootNavigation = createEvent()

sample({
  source: JingleFormGate.state,
  clock: [jingleCreate.finished.success, jingleUpdate.finished.success],
  filter: (state: JingleFormGateProps) => !state.isModal,
  target: AudioRootNavigation,
})

sample({
  clock: AudioRootNavigation,
  fn: () => ({ to: ROUTES.AUDIO.ROOT }),
  target: navigateToFx,
})
